import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import type { ArBillingNote } from '@dcc-erp/shared';
import { toNumber } from '@dcc-erp/shared';

import { BillingNoteList } from '../components/ar/BillingNoteList';
import { CreateBillingNoteModal } from '../components/ar/CreateBillingNoteModal';
import { RecordReceiptModal } from '../components/ar/RecordReceiptModal';
import { PageHead } from '../components/layout';
import { downloadFile } from '../lib/api';
import {
  useActiveCompanyId,
  useBillingNote,
  useBillingNotes,
  useCancelBillingNote,
  useIssueBillingNote,
} from '../lib/queries';

export const Route = createFileRoute('/ar/billing-notes')({ component: ArBillingNotesPage });

export function ArBillingNotesPage() {
  const companyId = useActiveCompanyId();
  const { data, isPending, error } = useBillingNotes(companyId);
  const issue = useIssueBillingNote(companyId);
  const cancel = useCancelBillingNote(companyId);
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  // Receipt shortcut: the id of the billing note whose receipt modal is open
  // (รับชำระจากใบวางบิล). The detail (lines + live outstanding) is fetched lazily
  // in ReceiptFromBillingNote so we preset only invoices that still have a
  // balance. null = closed.
  const [receiptBnId, setReceiptBnId] = useState<string | null>(null);

  function handlePrint(bn: ArBillingNote) {
    void downloadFile(
      `/companies/${companyId}/ar/billing-notes/${bn.id}/billing-note.pdf`,
      `${bn.voucherNo}.pdf`,
    );
  }

  function handleCancel(bn: ArBillingNote) {
    const reason = window.prompt('เหตุผลในการยกเลิกใบวางบิล');
    if (!reason) return;
    void cancel.mutateAsync({ id: bn.id, reason });
  }

  return (
    <div className="page">
      <PageHead
        crumbs={['ลูกหนี้การค้า', 'ใบวางบิล']}
        title="ใบวางบิล (Billing Notes)"
        actions={
          <button className="btn primary" onClick={() => setCreateOpen(true)} disabled={!companyId}>
            สร้างใบวางบิล
          </button>
        }
      />
      {isPending && (
        <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
          กำลังโหลด…
        </div>
      )}
      {error && (
        <div className="neg" style={{ padding: 24, textAlign: 'center' }}>
          โหลดไม่สำเร็จ: {error.message}
        </div>
      )}
      {data && (
        <BillingNoteList
          billingNotes={data}
          onView={handlePrint}
          onIssue={(bn) => void issue.mutateAsync(bn.id)}
          onCancel={handleCancel}
          onPrint={handlePrint}
          onReceive={(bn) => setReceiptBnId(bn.id)}
        />
      )}

      {createOpen && companyId && (
        <CreateBillingNoteModal companyId={companyId} onClose={() => setCreateOpen(false)} />
      )}

      {receiptBnId && companyId && (
        <ReceiptFromBillingNote
          companyId={companyId}
          billingNoteId={receiptBnId}
          onClose={() => {
            setReceiptBnId(null);
            // A receipt the existing modal records invalidates invoices/receipts,
            // but the billing-note list (its derived `collected` badge) keys off
            // the linked invoices' live outstanding — refresh it so the badge
            // flips once everything is collected.
            void qc.invalidateQueries({ queryKey: ['ar-billing-notes', companyId] });
          }}
        />
      )}
    </div>
  );
}

// Receipt shortcut wiring (รับชำระจากใบวางบิล). Fetches the billing-note detail
// so we can preset the receipt modal with this note's customer + the line
// invoices that are STILL outstanding (a line already paid since the note was
// issued — current `outstanding` 0 — is skipped). Reuses RecordReceiptModal as
// `/ar` does; adds NO receipt logic. While the detail loads we render nothing
// (the modal opens once the lines are known).
function ReceiptFromBillingNote({
  companyId,
  billingNoteId,
  onClose,
}: {
  companyId: string;
  billingNoteId: string;
  onClose: () => void;
}) {
  const { data: bn } = useBillingNote(companyId, billingNoteId);

  // Line invoice ids whose LIVE outstanding is still > 0 — those are the ones
  // worth presetting (skip any line fully paid since issuance).
  const presetInvoiceIds = useMemo(
    () => (bn?.lines ?? []).filter((l) => toNumber(l.outstanding) > 0).map((l) => l.invoiceId),
    [bn],
  );

  if (!bn) return null;

  return (
    <RecordReceiptModal
      companyId={companyId}
      presetCustomerId={bn.customerId}
      presetInvoiceIds={presetInvoiceIds}
      onClose={onClose}
    />
  );
}
